import React, { useState, useEffect, useCallback } from 'react';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../../components/NoData/NoData';
import {
  MessageBox,
  Form,
  Fieldset,
  TextInput,
  Dropdown,
  Button,
  ResetButton,
  Label,
} from '../../../components/FormElements';
import { TableBuilder } from '../../../components/TableElements';
import { StyledFiltersContainer } from '../../../components/Styled';
import Pagination from '../../../components/Pagination/Pagination';
import API from '../../../api';
import { API_RESPONSE_TYPES } from '../../../constants';
import {
  getSearchParams,
  formatCurrency,
  shouldRedirect,
  redirectToPageOne,
  downloadExportFile,
} from '../../../utils';
import FiltersButton from '../../../components/FiltersButton/FiltersButton';

const APIProviderPaymentsTable = (props) => {
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 10,
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [apiProviderPayments, setApiProviderPayments] = useState([]);
  const [apiProviders, setApiProviders] = useState([]);
  const [apiProvidersId, setApiProvidersId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const resetForm = () => {
    setApiProvidersId('');
    setStartDate('');
    setEndDate('');
    readData();
  };

  const readApiProviders = () => {
    API.get('/recharge-configurations/api-providers').then((response) => {
      const { status, data } = response.data;
      if (status === API_RESPONSE_TYPES.SUCCESS) {
        setApiProviders(data);
      }
    });
  };

  const readData = useCallback(
    (apiProvidersId = '', startDate = '', endDate = '') => {
      setIsLoading(true);
      setResponseStatus('');
      setResponseMessage('');
      API.get(
        `/accounting/api-provider-payments/?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fApiProvidersId=${apiProvidersId}&fStartDate=${startDate}&fEndDate=${endDate}`
      )
        .then((response) => {
          const { status, data, pageInfo } = response.data;
          if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
            redirectToPageOne();
          } else {
            if (status === API_RESPONSE_TYPES.SUCCESS) {
              setTotalRecords(pageInfo.totalRecords);
            }
            setApiProviderPayments(data);
          }
        })
        .catch((error) => {
          setResponseStatus(API_RESPONSE_TYPES.FAILURE);
          setResponseMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [paginationData]
  );

  const reloadData = () => {
    readData(apiProvidersId, startDate, endDate);
  };

  const deleteData = (id) => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.delete(
      `/accounting/api-provider-payments/${id}?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fApiProvidersId=${apiProvidersId}&fStartDate=${startDate}&fEndDate=${endDate}`
    )
      .then((response) => {
        const { status, message, data } = response.data;
        if (shouldRedirect(data?.length, paginationData?.pageNumber)) {
          redirectToPageOne();
        } else {
          if (status === API_RESPONSE_TYPES.FAILURE) {
            setResponseStatus(status);
            setResponseMessage(message);
          } else {
            setApiProviderPayments(data);
          }
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILURE);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // const onClickExport = () => {
  //   setIsLoading(true);
  //   setResponseStatus('');
  //   setResponseMessage('');
  //   API.get(
  //     `/accounting/api-provider-payments/export?fApiProvidersId=${apiProvidersId}&fStartDate=${startDate}&fEndDate=${endDate}`
  //   )
  //     .then((response) => {
  //       const { status, message } = response.data;
  //       if (status === API_RESPONSE_TYPES.FAILURE) {
  //         setResponseStatus(status);
  //         setResponseMessage(message);
  //       } else {
  //         setResponseStatus(status);
  //         setResponseMessage(message);
  //         setTimeout(() => {
  //           setResponseStatus('');
  //           setResponseMessage('');
  //         }, 3000);
  //       }
  //     })
  //     .catch((error) => {
  //       setResponseStatus(API_RESPONSE_TYPES.FAILURE);
  //       setResponseMessage(error.message);
  //     })
  //     .finally(() => {
  //       setIsLoading(false);
  //     });
  // };

  const onClickExport = () => {
    downloadExportFile({
      url: `/accounting/api-provider-payments/export?fApiProvidersId=${apiProvidersId || ''}&fStartDate=${startDate || ''}&fEndDate=${endDate || ''}`,
      fileName: 'api_provider_payments.csv',
      setIsLoading,
      setResponseStatus,
      setResponseMessage,
    });
  };

  const filterData = (e) => {
    e.preventDefault();
    setIsFiltering(true);
  };

  useEffect(() => {
    if (isFiltering) {
      setPaginationData((paginationData) => ({
        pageNumber: 1,
        recordsPerPage: paginationData.recordsPerPage,
      }));
      setIsFiltering((isFiltering) => !isFiltering);
    }
  }, [isFiltering]);

  useEffect(() => {
    readApiProviders();
  }, []);

  useEffect(() => {
    if (!isFiltering) {
      readData(apiProvidersId, startDate, endDate);
    }
  }, [paginationData, readData]);

  const apiProvidersOptions = [
    { title: '-- SELECT API PROVIDER --', value: '' },
  ];
  apiProviders.forEach(({ id, providerName }) =>
    apiProvidersOptions.push({
      title: providerName,
      value: id,
    })
  );

  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };

  return (
    <>
      <PageHeading
        {...props}
        reloadData={reloadData}
        showExport={true}
        onClickExport={onClickExport}
      />
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledFiltersContainer className={showFilters ? 'show' : ''}>
        <Form method="POST" action="#" onSubmit={filterData}>
          <Fieldset>
            <Label>API Provider</Label>
            <Dropdown
              style={{ border: '1px solid #cacaca' }}
              placeholder="API Provider"
              value={apiProvidersId}
              onChange={setApiProvidersId}
              options={apiProvidersOptions}
              disabled={isLoading}
            />
          </Fieldset>
          <Fieldset>
            <Label>Start Date</Label>
            <TextInput
              type="date"
              value={startDate}
              onChange={setStartDate}
              placeholder="Start Date"
              disabled={isLoading === true}
            />
          </Fieldset>
          <Fieldset>
            <Label>End Date</Label>
            <TextInput
              type="date"
              value={endDate}
              onChange={setEndDate}
              placeholder="End Date"
              disabled={isLoading === true}
            />
          </Fieldset>
          <Fieldset>
            <Button disabled={isLoading === true} type="submit">
              Filter
            </Button>
            <ResetButton
              disabled={isLoading === true}
              type="button"
              onClick={resetForm}
            >
              Clear
            </ResetButton>
          </Fieldset>
        </Form>
      </StyledFiltersContainer>
      {responseStatus && responseMessage && (
        <MessageBox status={responseStatus} message={responseMessage} />
      )}
      <LoadingSpinner
        isLoading={
          responseStatus === '' &&
          apiProviderPayments?.length === 0 &&
          isLoading
        }
      />
      <NoData
        status={
          responseStatus !== API_RESPONSE_TYPES.FAILURE &&
          !isLoading &&
          apiProviderPayments?.length === 0
        }
        message={`No API provider payments found`}
      />
      {apiProviderPayments?.length > 0 && (
        <>
          <TableBuilder
            isLoading={apiProviderPayments?.length !== 0 && isLoading}
            tableHeadings={[
              {
                title: 'API Provider',
                dataSelector: 'providerName',
                dataType: 'string',
              },
              {
                title: 'UTR / Reference Number',
                dataSelector: 'utrNumber',
                dataType: 'string',
              },
              {
                title: 'Amount',
                dataSelector: 'amount',
                dataType: 'number',
                CellRenderer: (value) => formatCurrency(value),
              },
              {
                title: 'Transaction Date',
                dataSelector: 'transactionDate',
                dataType: 'date',
              },
            ]}
            tableData={apiProviderPayments}
          />
          <Pagination
            totalRecords={totalRecords}
            paginationData={paginationData}
            setPaginationData={setPaginationData}
          />
        </>
      )}
    </>
  );
};

export default withAppHOC(APIProviderPaymentsTable);
